import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, FormGroup, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FUND_INVESTMENTS_ENABLER } from 'common/constants/features';
import {
  ENABLE_DAILY_NAV,
  ENABLE_DAILY_NAV_TOOLTIP,
  ENABLE_FUND_INVESTMENTS,
  ENABLE_FUND_INVESTMENTS_TOOLTIP,
  FEATURE_SETTINGS_PAGE_TITLE,
  FIRM_SETTINGS,
  REQUIRES_2FA,
  REQUIRES_2FA_TOOLTIP,
  SHOW_PROCESS_MANAGEMENT_COLUMN,
  SHOW_PROCESS_MANAGEMENT_COLUMN_TOOLTIP,
  USE_SELECTED_NUMBER_FORMAT_FOR_INPUTS,
  USE_SELECTED_NUMBER_FORMAT_FOR_INPUTS_TOOLTIP,
} from 'common/constants/firms';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { buildFirmSettingsNavigationItems } from 'pages/Firms/firmSettingsNavigation';
import { useReadUserAccessLevel } from 'services/hooks';
import { useUpdateFirmFeatureSettings } from 'services/hooks/firmFeatureSettings';
import FeatureSettingCheckbox from './FeatureSettingCheckbox';

interface FeatureSettingsState {
  areFundInvestmentsEnabled: boolean;
  dailyNavEnabled: boolean;
  requires2fa: boolean;
  showProcessManagementColumn: boolean;
  useSelectedNumberFormatForInputs: boolean;
}

const INITIAL_SETTINGS: FeatureSettingsState = {
  areFundInvestmentsEnabled: false,
  dailyNavEnabled: false,
  requires2fa: false,
  showProcessManagementColumn: false,
  useSelectedNumberFormatForInputs: false,
};

const FeatureSettings = () => {
  const [{ firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();
  const [, checkFeature, areFeaturesReady] = useCheckFeatures();
  const { isAdminOrStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });
  const { updateFirmFeatureSettings, isPending } = useUpdateFirmFeatureSettings();
  const [settings, setSettings] = useState(INITIAL_SETTINGS);

  const firmId = firmInfo?.id ?? null;
  const firmSlug = firmInfo?.slug ?? '';
  const isUserStaff = Boolean(user?.is_staff);
  const showFundInvestments =
    isUserStaff && areFeaturesReady && checkFeature(FUND_INVESTMENTS_ENABLER);
  const showDailyNav = isUserStaff;
  const isReadOnly = !isAdminOrStaffOrSuperuser;

  // Rehydrate local controls when firm context changes so settings from the previous firm cannot leak into this page.
  useEffect(() => {
    if (!firmInfo) {
      return;
    }

    setSettings({
      areFundInvestmentsEnabled: firmInfo.are_fund_investments_enabled ?? false,
      dailyNavEnabled: firmInfo.daily_nav_enabled ?? false,
      requires2fa: firmInfo.requires_2fa ?? false,
      showProcessManagementColumn: firmInfo.show_process_management_column ?? false,
      useSelectedNumberFormatForInputs: firmInfo.use_selected_number_format_for_inputs ?? false,
    });
  }, [firmInfo]);

  const updateSetting = useCallback(
    (key: keyof FeatureSettingsState) => (checked: boolean) => {
      setSettings(current => ({ ...current, [key]: checked }));
    },
    []
  );

  const saveFeatureSettings = useCallback(async () => {
    if (!firmId || isReadOnly) {
      return;
    }

    const updatedFirm = await updateFirmFeatureSettings(firmId, {
      requires_2fa: settings.requires2fa,
      show_process_management_column: settings.showProcessManagementColumn,
      use_selected_number_format_for_inputs: settings.useSelectedNumberFormatForInputs,
      ...(showFundInvestments
        ? { are_fund_investments_enabled: settings.areFundInvestmentsEnabled }
        : {}),
      ...(showDailyNav ? { daily_nav_enabled: settings.dailyNavEnabled } : {}),
    });

    if (updatedFirm) {
      enqueueSnackbar('Feature Settings successfully saved', { variant: 'success' });
    }
  }, [
    firmId,
    isReadOnly,
    settings,
    showFundInvestments,
    showDailyNav,
    updateFirmFeatureSettings,
    enqueueSnackbar,
  ]);

  const secondaryNavigation = useMemo(
    () => ({
      title: FIRM_SETTINGS,
      items: buildFirmSettingsNavigationItems(firmSlug, {
        dailyNavEnabled: Boolean(firmInfo?.daily_nav_enabled),
      }),
      actions: isReadOnly
        ? []
        : [
          {
            type: 'button' as const,
            key: 'firm-feature-settings-save',
            label: 'Save',
            variant: 'positive' as const,
            onClick: saveFeatureSettings,
            disabled: !firmId || isPending,
          },
        ],
    }),
    [firmSlug, firmInfo?.daily_nav_enabled, isReadOnly, saveFeatureSettings, firmId, isPending]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  return (
    <Box sx={{ p: 3, maxWidth: 680 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {FEATURE_SETTINGS_PAGE_TITLE}
      </Typography>
      <FormGroup>
        <FeatureSettingCheckbox
          checked={settings.requires2fa}
          disabled={isReadOnly}
          id="feature-settings-requires-2fa"
          label={REQUIRES_2FA}
          tooltip={REQUIRES_2FA_TOOLTIP}
          onChange={updateSetting('requires2fa')}
        />
        <FeatureSettingCheckbox
          checked={settings.showProcessManagementColumn}
          disabled={isReadOnly}
          id="feature-settings-process-management"
          label={SHOW_PROCESS_MANAGEMENT_COLUMN}
          tooltip={SHOW_PROCESS_MANAGEMENT_COLUMN_TOOLTIP}
          onChange={updateSetting('showProcessManagementColumn')}
        />
        {showFundInvestments && (
          <FeatureSettingCheckbox
            checked={settings.areFundInvestmentsEnabled}
            disabled={isReadOnly}
            id="feature-settings-fund-investments"
            label={ENABLE_FUND_INVESTMENTS}
            tooltip={ENABLE_FUND_INVESTMENTS_TOOLTIP}
            onChange={updateSetting('areFundInvestmentsEnabled')}
          />
        )}
        {showDailyNav && (
          <FeatureSettingCheckbox
            checked={settings.dailyNavEnabled}
            disabled={isReadOnly}
            id="feature-settings-daily-nav"
            label={ENABLE_DAILY_NAV}
            tooltip={ENABLE_DAILY_NAV_TOOLTIP}
            onChange={updateSetting('dailyNavEnabled')}
          />
        )}
        <FeatureSettingCheckbox
          checked={settings.useSelectedNumberFormatForInputs}
          disabled={isReadOnly}
          id="feature-settings-selected-number-format-inputs"
          label={USE_SELECTED_NUMBER_FORMAT_FOR_INPUTS}
          tooltip={USE_SELECTED_NUMBER_FORMAT_FOR_INPUTS_TOOLTIP}
          onChange={updateSetting('useSelectedNumberFormatForInputs')}
        />
      </FormGroup>
    </Box>
  );
};

export default FeatureSettings;
